import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Arrange, Typography, SelectableTile, Link, Button } from "@aviary";
import { usePatientDiscountIncrementsQuery } from "@shared/data/queries/PatientDiscountIncrementsQuery/PatientDiscountIncrements.query";
import { l } from "@unauthenticated/oauth/locales/i18n";

import { OAuthStepHeader } from "../OAuthStepHeader";
import { OAuthStep } from "../oauthStep";

import * as styles from "./OAuthAccountTypeStep.styles";

interface Props {
  onUpdateCurrentStep: (step: OAuthStep) => void;
  onOnboardingComplete: () => void;
  onUpdateStoreCurrentSetupPage: (step: string) => Promise<void>;
}

const OAuthAccountTypeStep = ({
  onUpdateCurrentStep,
  onUpdateStoreCurrentSetupPage,
  onOnboardingComplete,
}: Props) => {
  const { t } = useTranslation();
  const [forProfit, setForProfit] = useState(null);
  const { data, loading: incrementsLoading } = usePatientDiscountIncrementsQuery();

  if (incrementsLoading) {
    return null;
  }

  const {
    currentStore: { patientDiscountIncrements },
  } = data;
  const maxValue = Math.max(...patientDiscountIncrements);

  const handleOnNextClick = async () => {
    if (forProfit) {
      onUpdateCurrentStep(OAuthStep.PAYMENT);
    } else {
      await onUpdateStoreCurrentSetupPage("complete");
      onOnboardingComplete();
    }
  };

  return (
    <div>
      <OAuthStepHeader currentStep={OAuthStep.ACCOUNT_TYPE} />
      <div css={styles.body}>
        <div css={styles.bodyTop}>
          <Typography isMarginless>{t(l.oauth.title)}</Typography>
          <Link
            href={t(l.oauth.learnMoreLink)}
            target="_blank"
            rel="noopener noreferrer"
            noExternalIcon
          >
            {t(l.oauth.learnMore)}
          </Link>
        </div>

        <Arrange isVertical>
          <SelectableTile
            name="account-type"
            aria-label={t(l.oauth.noMarginTitle)}
            isRadio
            isInline
            isContentHeight
            value="no-margin"
            onChange={() => setForProfit(false)}
            checked={forProfit !== null && !forProfit}
          >
            <Typography className="has-text-weight-bold">{t(l.oauth.noMarginTitle)}</Typography>
            <ul css={styles.list}>
              <li>
                <Typography>{t(l.oauth.noMarginDescription1)}</Typography>
              </li>
              <li>
                <Typography>{t(l.oauth.noMarginDescription2)}</Typography>
              </li>
              <li>
                <Typography>{t(l.oauth.noMarginDescription3)}</Typography>
              </li>
            </ul>
          </SelectableTile>
          <SelectableTile
            name="account-type"
            aria-label={t(l.oauth.marginTitle)}
            isRadio
            isInline
            isContentHeight
            value="margin"
            onChange={() => setForProfit(true)}
            checked={forProfit}
          >
            <Typography className="has-text-weight-bold">{t(l.oauth.marginTitle)}</Typography>
            <ul css={styles.list}>
              <li>
                <Typography>{t(l.oauth.marginDescription1, { maxValue })}</Typography>
              </li>
              <li>
                <Typography>{t(l.oauth.marginDescription2)}</Typography>
              </li>
              <li>
                <Typography>
                  <Trans i18nKey={l.oauth.marginDescription3}>
                    This account type can <strong>not</strong> be changed to a non profit later
                  </Trans>
                </Typography>
              </li>
            </ul>
          </SelectableTile>
        </Arrange>
        <Button
          css={styles.button}
          isColor="primary"
          disabled={forProfit === null}
          onClick={handleOnNextClick}
        >
          {t(l.oauth.nextButton)}
        </Button>
      </div>
    </div>
  );
};

export { OAuthAccountTypeStep };
